import platform from 'platform';
import { useCallback, useRef } from 'react';

import {
  InfringementContextValue,
  InfringementSeverity,
  InfringementSource,
} from '../../../types/certifications/Certification';
import { useSendInfringementMutation } from '../certificationApiSlice';

const DEDUPE_WINDOW_MS = 800;

type ViolationInput = {
  rule: string;
  source: InfringementSource;
  severity: InfringementSeverity;
  title: string;
  description: string;
  occurredAt?: string;
  context?: Record<string, InfringementContextValue> | null;
};

function normalizeOccurrence(occurredAt?: string) {
  if (occurredAt) {
    const parsed = Date.parse(occurredAt);

    if (!Number.isNaN(parsed)) {
      return {
        occurredAtIso: new Date(parsed).toISOString(),
        occurredTs: parsed,
      };
    }
  }

  const occurredTs = Date.now();
  return {
    occurredAtIso: new Date(occurredTs).toISOString(),
    occurredTs,
  };
}

export function useViolationLogger(certificationLogId: string) {
  const [sendInfringement] = useSendInfringementMutation();
  const lastRef = useRef<{ key: string; at: number } | null>(null);

  const logViolation = useCallback(
    async ({
      rule,
      source,
      severity,
      title,
      description,
      occurredAt,
      context,
    }: ViolationInput) => {
      if (!certificationLogId) return;

      const now = Date.now();
      const dedupeKey = `${source}:${rule}`;

      if (
        lastRef.current &&
        lastRef.current.key === dedupeKey &&
        now - lastRef.current.at < DEDUPE_WINDOW_MS
      ) {
        return;
      }

      lastRef.current = { key: dedupeKey, at: now };
      const { occurredAtIso, occurredTs } = normalizeOccurrence(occurredAt);

      try {
        await sendInfringement({
          certification_log: certificationLogId,
          browser: { version: platform.description || '' },
          contract_version: 'v2',
          rule,
          source,
          severity,
          title,
          description,
          occurred_at: occurredAtIso,
          occurred_ts: occurredTs,
          context: context ?? null,
        }).unwrap();
      } catch {
        // Non-blocking by design
      }
    },
    [certificationLogId, sendInfringement],
  );

  return { logViolation };
}
